/**
 * useColleaguePresence — live collega-lijst voor de doorverbind-picker.
 *
 * Leest GET /api/telephony/presence?all=1 (open voor elke office-gebruiker
 * sinds doorverbinden) en leidt per collega een kiesbaarheid af met dezelfde
 * regels als de routering (vers-busy < 5 min, presence, provisioning).
 * Fetcht bij mount (= openen van de picker) en ververst elke ~15s zolang de
 * picker open staat.
 */

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export type ColleagueAvailability =
  | 'beschikbaar'
  | 'in-gesprek'
  | 'niet-storen'
  | 'geen-registratie';

export interface Colleague {
  userId: string;
  email: string;
  name: string;
  availability: ColleagueAvailability;
  /** Alleen 'beschikbaar' is direct kiesbaar. */
  selectable: boolean;
}

const REFRESH_MS = 15_000;
// Zelfde vers-criterium als de routering (telephony-routing.ts).
const BUSY_FRESH_MS = 5 * 60_000;

interface PresenceRow {
  user_id: string;
  user_email: string;
  display_name: string | null;
  presence: string;
  ring_enabled: boolean;
  busy_since: string | null;
  provisioned: boolean;
  /** Heeft nu écht een lopend gesprek (calls-tabel). Ontbreekt bij een
   *  oudere API of gefaalde calls-query — dan telt alleen busy_since. */
  has_active_call?: boolean;
}

function deriveAvailability(row: PresenceRow): ColleagueAvailability {
  if (!row.provisioned) return 'geen-registratie';
  if (row.presence === 'dnd') return 'niet-storen';
  // busy_since is een heartbeat-vlag die tot 5 min na het laatste gesprek
  // kan nahangen (belproef 30-07: collega onterecht grijs vlak na een
  // transfer). "In gesprek" alleen als de vlag vers is ÉN er echt een
  // lopend gesprek is; dezelfde dubbelcheck zit server-side in transfer.ts.
  const busyMs = row.busy_since ? Date.now() - new Date(row.busy_since).getTime() : Infinity;
  if (busyMs < BUSY_FRESH_MS && row.has_active_call !== false) return 'in-gesprek';
  return 'beschikbaar';
}

export function useColleaguePresence(options?: { excludeUserId?: string | null }) {
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const excludeUserId = options?.excludeUserId ?? null;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Niet ingelogd');
        const r = await fetch('/api/telephony/presence?all=1', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!r.ok) throw new Error(`Collega-lijst laden mislukt (${r.status})`);
        const json = (await r.json()) as { presences?: PresenceRow[] };
        if (cancelled) return;
        const list = (json.presences ?? [])
          .filter((row) => row.user_id !== excludeUserId)
          .map((row): Colleague => {
            const availability = deriveAvailability(row);
            return {
              userId: row.user_id,
              email: row.user_email,
              name: row.display_name || row.user_email.split('@')[0] || row.user_email,
              availability,
              selectable: availability === 'beschikbaar',
            };
          })
          .sort((a, b) => {
            if (a.selectable !== b.selectable) return a.selectable ? -1 : 1;
            return a.name.localeCompare(b.name, 'nl');
          });
        setColleagues(list);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [excludeUserId]);

  return { colleagues, isLoading, error };
}
